import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Calculator, TruckIcon, AlertCircle, Check, Mail, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import BackButton from '../components/BackButton';
import { trackDevisSubmit } from '../components/GoogleAnalytics';
import { trackGenerateLead, trackCalculatorUsed } from '../utils/analytics';
import { executeReCaptcha } from '../utils/recaptcha';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import LanguageSwitcher from '../components/LanguageSwitcher';
import VilleAutocomplete from '../components/VilleAutocomplete';
import AntiSpamFields from '../components/AntiSpamFields';

export default function Devis() {
  const { t } = useTranslation();

  // SEO meta tags - direct DOM manipulation for reliable overriding
  useEffect(() => {
    document.title = 'Calculateur Prix Béton Décoratif | Devis Gratuit en Ligne | DumaExpertBéton';
    const descMeta = document.querySelector('meta[name="description"]');
    if (descMeta) descMeta.setAttribute('content', 'Calculez le prix de votre béton imprimé, désactivé ou balayé en 30 secondes. Devis gratuit et personnalisé sous 24h. Expert depuis 2014, zone PACA.');
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = 'https://duma-expert-beton.fr/devis';
    return () => { document.title = 'DumaExpertBéton - Expert en Béton Imprimé et Décoratif | Orange, Vaucluse'; };
  }, []);

  const [prixTypes, setPrixTypes] = useState([]);
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    surface: '',
    epaisseur_cm: '12', // Înălțime beton în cm (default 12cm)
    type_beton: '',
    prix_m2: '',
    besoin_pompe: false,
    volume_beton: '',
    besoin_terasament: false,
    surface_terasament: '', // m² exact de terasament nécessaire
    ville: '',
    notes: '',
    gdpr_consent: false,
    honeypot: '' // Honeypot field
  });
  const [calculation, setCalculation] = useState(null);
  const [submissionStatus, setSubmissionStatus] = useState(null); // null | 'created' | 'updated'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [showDataModal, setShowDataModal] = useState(false);
  const [pendingCalculation, setPendingCalculation] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const antiSpamRef = React.useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchPrixTypes();
  }, []);

  const fetchPrixTypes = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/devis/prix-types`);
      setPrixTypes(response.data.types);
    } catch (error) {
      console.error('Error fetching prix types:', error);
    }
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    const name = e.target.name;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-recalculate prix/m² when surface changes (based on tiers)
    if (name === 'surface' && formData.type_beton) {
      const selectedType = prixTypes.find(t => t.label === formData.type_beton);
      if (selectedType) {
        const nouveauPrix = calculatePrixM2(selectedType.id, value);
        setFormData(prev => ({
          ...prev,
          prix_m2: nouveauPrix.toString()
        }));
      }
    }

    // Auto-calculate volume when surface changes (if pompe is checked)
    if (name === 'surface' && formData.besoin_pompe) {
      const surface = parseFloat(value) || 0;
      const epaisseur_cm = parseFloat(formData.epaisseur_cm) || 12;
      const volumeEstime = (surface * (epaisseur_cm / 100)).toFixed(2);
      setFormData(prev => ({
        ...prev,
        volume_beton: volumeEstime
      }));
    }
    
    // Auto-calculate volume when epaisseur changes
    if (name === 'epaisseur_cm' && formData.besoin_pompe && formData.surface) {
      const surface = parseFloat(formData.surface) || 0;
      const epaisseur_cm = parseFloat(value) || 12;
      const volumeEstime = (surface * (epaisseur_cm / 100)).toFixed(2);
      setFormData(prev => ({
        ...prev,
        volume_beton: volumeEstime
      }));
    }

    // Reset volume if pompe is unchecked
    if (name === 'besoin_pompe' && !value) {
      setFormData(prev => ({
        ...prev,
        volume_beton: ''
      }));
      setShowWarning(false);
    }

    // Auto-calculate volume when pompe is checked
    if (name === 'besoin_pompe' && value && formData.surface) {
      const surface = parseFloat(formData.surface) || 0;
      const epaisseur_cm = parseFloat(formData.epaisseur_cm) || 12;
      const volumeEstime = (surface * (epaisseur_cm / 100)).toFixed(2);
      setFormData(prev => ({
        ...prev,
        volume_beton: volumeEstime
      }));
    }

    // Check volume warning
    if (name === 'volume_beton' && parseFloat(value) > 32) {
      setShowWarning(true);
    } else if (name === 'volume_beton') {
      setShowWarning(false);
    }
  };

  const calculatePrixM2 = (typeId, surface) => {
    const selectedType = prixTypes.find(t => t.id === typeId);
    if (!selectedType || !selectedType.prix_tiers) return 149; // default to highest tier
    
    const surfaceNum = parseFloat(surface) || 0;
    
    // Find the correct tier
    for (const tier of selectedType.prix_tiers) {
      if (surfaceNum >= tier.min_surface && (tier.max_surface === null || surfaceNum <= tier.max_surface)) {
        return tier.prix;
      }
    }
    
    return 149; // fallback to highest tier
  };

  const handleTypeBetonChange = (value) => {
    const selectedType = prixTypes.find(t => t.id === value);
    const surface = parseFloat(formData.surface) || 0;
    const prix = calculatePrixM2(value, surface);
    
    setFormData(prev => ({
      ...prev,
      type_beton: selectedType?.label || '',
      prix_m2: prix.toString()
    }));
  };

  const calculatePrice = () => {
    const surface = parseFloat(formData.surface) || 0;
    const epaisseur_cm = parseFloat(formData.epaisseur_cm) || 12;
    const prixM2 = parseFloat(formData.prix_m2) || 0;
    
    // Validare: suprafață minimă
    if (surface < 20) {
      alert(t('devis.minimumSurface'));
      return;
    }
    
    // Calcul volum real în m³: Suprafață (m²) × Înălțime (cm) / 100
    const volumeBeton = surface * (epaisseur_cm / 100);

    // Cost beton bazat pe suprafață și preț/m²
    let cout_lucrare = surface * prixM2;
    let cout_pompe = 0;
    let cout_terasament = 0;

    // Cost pompă bazat pe volum real
    if (formData.besoin_pompe && volumeBeton > 0) {
      cout_pompe = 500 + (20 * volumeBeton);
    }

    // Cost terasament bazat pe m² real necesari
    if (formData.besoin_terasament && formData.surface_terasament) {
      const surface_terasament = parseFloat(formData.surface_terasament) || 0;
      cout_terasament = 33 * surface_terasament;
    }

    const total_estime = cout_lucrare + cout_pompe + cout_terasament;

    const calculatedResult = {
      cout_lucrare: cout_lucrare.toFixed(2),
      cout_pompe: cout_pompe.toFixed(2),
      cout_terasament: cout_terasament.toFixed(2),
      volume_m3: volumeBeton.toFixed(2),
      epaisseur_cm: epaisseur_cm,
      total_estime: total_estime.toFixed(2)
    };
    
    // Update formData cu volumul calculat
    setFormData(prev => ({
      ...prev,
      volume_beton: volumeBeton.toFixed(2)
    }));
    
    // Store calculation temporarily and show data collection modal
    setPendingCalculation(calculatedResult);
    setShowDataModal(true);
  };


  // Function: Handle modal close with abandon tracking
  const handleModalClose = async () => {
    // Log abandon event to backend
    try {
      await axios.post(`${BACKEND_URL}/api/devis/abandon`, {
        calculation: pendingCalculation,
        surface: formData.surface,
        type_beton: formData.type_beton,
        timestamp: new Date().toISOString(),
        source: window.location.href
      });
      console.log('📊 Abandon event logged');
    } catch (error) {
      console.error('Error logging abandon:', error);
    }
    
    // Close modal
    setShowDataModal(false);
    setPendingCalculation(null);
  };

  // New function: Submit lead data and reveal calculation
  const handleRevealCalculation = async () => {
    // Validate required fields
    if (!formData.email || !formData.gdpr_consent) {
      alert(t('devis.fillRequiredFields'));
      return;
    }
    
    // Honeypot check
    if (formData.honeypot) {
      console.log('Bot detected - honeypot filled');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const recaptchaToken = await executeReCaptcha('devis_lead');
      
      // Prepare lead data with calculation
      const leadData = {
        email: formData.email,
        prenom: formData.prenom || '',
        nom: formData.nom || '',
        telephone: formData.telephone || '',
        ville: formData.ville || '',
        surface: formData.surface,
        epaisseur_cm: formData.epaisseur_cm,
        type_beton: formData.type_beton,
        prix_m2: formData.prix_m2,
        besoin_pompe: formData.besoin_pompe,
        besoin_terasament: formData.besoin_terasament,
        surface_terasament: formData.surface_terasament || '',
        volume_beton: formData.volume_beton,
        notes: formData.notes || '',
        calculation: pendingCalculation,
        gdpr_consent: formData.gdpr_consent,
        source: window.location.href,
        recaptchaToken,
        cf_turnstile_token: turnstileToken,
        website: antiSpamRef.current?.getHoneypotValue() || ''
      };
      
      // Send to backend
      const response = await axios.post(`${BACKEND_URL}/api/devis/lead`, leadData);
      
      if (response.status === 200 || response.status === 201) {
        // Success: close modal, show calculation
        setShowDataModal(false);
        setCalculation(pendingCalculation);
        setSubmissionStatus(response.data?.updated ? 'updated' : 'created');
        trackDevisSubmit(pendingCalculation.total_estime);
        trackGenerateLead('devis_submit');
        trackCalculatorUsed();
      }
      
    } catch (error) {
      console.error('Error submitting lead:', error);
      alert(t('devis.errorSendingData'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot check
    if (formData.honeypot) {
      console.log('Bot detected - honeypot filled');
      return;
    }
    
    if (!formData.gdpr_consent) {
      alert(t('devis.acceptPrivacy'));
      return;
    }

    if (!calculation) {
      alert(t('devis.calculateFirst'));
      return;
    }

    setIsSubmitting(true);

    try {
      // Get reCAPTCHA token
      const recaptchaToken = await executeReCaptcha('devis_submit');
      
      const submitData = {
        ...formData,
        surface: parseFloat(formData.surface),
        prix_m2: parseFloat(formData.prix_m2),
        volume_beton: formData.besoin_pompe ? parseFloat(formData.volume_beton) || 0 : null,
        recaptchaToken,
        cf_turnstile_token: turnstileToken,
        website: antiSpamRef.current?.getHoneypotValue() || ''
      };

      await axios.post(`${BACKEND_URL}/api/devis`, submitData);
      
      // Track conversion in Google Analytics
      trackDevisSubmit({
        ...submitData,
        total_estime: calculation.total_estime
      });
      trackGenerateLead('devis_submit');
      
      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting devis:', error);
      alert(t('contact.errorMessage'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <>
        <div className="fixed top-4 left-4 z-50">
          <BackButton />
        </div>
        <div className="fixed top-4 right-4 z-50">
          <LanguageSwitcher />
        </div>
        <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center px-4 transition-colors">
        <Card className="max-w-2xl w-full bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 transition-colors">
          <CardHeader className="text-center">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-10 h-10 text-white" />
            </div>
            <CardTitle className="text-3xl text-transparent bg-clip-text bg-gradient-to-r from-terracotta-400 to-clay-400">
              {t('devis.successTitle')}
            </CardTitle>
            <CardDescription className="text-slate-700 dark:text-slate-300 mt-4 text-lg transition-colors">
              {t('devis.successMessage')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {submissionStatus && (
              <div
                data-testid={`devis-status-banner-${submissionStatus}`}
                className={
                  submissionStatus === 'updated'
                    ? 'flex items-start gap-3 p-4 rounded-lg border-2 border-blue-500/40 bg-blue-500/10 text-blue-100'
                    : 'flex items-start gap-3 p-4 rounded-lg border-2 border-green-500/40 bg-green-500/10 text-green-100'
                }
              >
                <Check className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold">
                    {submissionStatus === 'updated'
                      ? t('devis.statusUpdated', 'Devis mis à jour')
                      : t('devis.statusCreated', 'Devis envoyé')}
                  </p>
                  <p className="text-sm opacity-90">
                    {submissionStatus === 'updated'
                      ? t(
                          'devis.statusUpdatedDetail',
                          "Votre nouveau calcul a bien été enregistré. Vous recevrez une mise à jour par email."
                        )
                      : t(
                          'devis.statusCreatedDetail',
                          'Consultez votre boîte de réception pour le détail.'
                        )}
                  </p>
                </div>
              </div>
            )}
            {calculation && (
              <div className="bg-slate-900 p-6 rounded-lg border border-slate-700">
                <h3 className="text-lg font-semibold text-slate-200 mb-3">{t('devis.summary')}</h3>
                <div className="space-y-2 text-slate-300">
                  {calculation.volume_m3 && (
                    <div className="flex justify-between bg-slate-800 p-2 rounded">
                      <span>{t('devis.volumeConcrete')} ({calculation.epaisseur_cm}cm {t('devis.volumeDetail')}):</span>
                      <span className="font-bold text-terracotta-400">{calculation.volume_m3} m³</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>{t('devis.concreteCost')}</span>
                    <span className="font-semibold">{calculation.cout_lucrare} €</span>
                  </div>
                  {formData.besoin_terasament && parseFloat(calculation.cout_terasament) > 0 && (
                    <div className="flex justify-between">
                      <span>{t('devis.groundCost')} ({formData.surface_terasament}m²):</span>
                      <span className="font-semibold">{calculation.cout_terasament} €</span>
                    </div>
                  )}
                  {formData.besoin_pompe && parseFloat(calculation.cout_pompe) > 0 && (
                    <div className="flex justify-between">
                      <span>{t('devis.pumpCost2')}</span>
                      <span className="font-semibold">{calculation.cout_pompe} €</span>
                    </div>
                  )}
                  <div className="border-t border-slate-700 pt-2 mt-2 flex justify-between text-lg">
                    <span className="font-bold text-slate-100">{t('devis.total')}</span>
                    <span className="font-bold text-green-400">{calculation.total_estime} €</span>
                  </div>
                </div>
              </div>
            )}
            <div className="flex gap-3">
              <Button 
                onClick={() => window.location.href = '/'}
                className="flex-1 bg-terracotta-600 hover:bg-terracotta-700"
              >
                {t('devis.backHome')}
              </Button>
              <Button 
                onClick={() => {
                  setIsSubmitted(false);
                  setCalculation(null);
                  setFormData({
                    nom: '',
                    prenom: '',
                    email: '',
                    telephone: '',
                    surface: '',
                    epaisseur_cm: '12',
                    type_beton: '',
                    prix_m2: '',
                    besoin_pompe: false,
                    volume_beton: '',
                    besoin_terasament: false,
                    surface_terasament: '',
                    ville: '',
                    notes: '',
                    gdpr_consent: false,
                    honeypot: ''
                  });
                }}
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                {t('devis.newQuote')}
              </Button>
            </div>
          </CardContent>
        </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="fixed top-4 left-4 z-50">
        <BackButton />
      </div>
      <div className="fixed top-4 right-4 z-50 flex gap-2">
        <LanguageSwitcher />
      </div>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 py-12 px-4 sm:px-6 lg:px-8 transition-colors">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white mb-4 transition-colors">
              {t('devis.title')}
            </h1>
            <p className="text-xl text-slate-700 dark:text-slate-100 transition-colors">
              {t('devis.subtitle')}
            </p>
          </div>

          <div>
            <div className="max-w-3xl mx-auto">
              {/* Calculator Card - Centered */}
              <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 transition-colors shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-slate-900 dark:text-slate-200 transition-colors">
                    <Calculator className="w-5 h-5 text-terracotta-400" />
                    {t('devis.projectDetails')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-slate-700 dark:text-slate-300 transition-colors">{t('devis.concreteType')}</Label>
                    <Select onValueChange={handleTypeBetonChange} required>
                      <SelectTrigger className="bg-slate-50 dark:bg-slate-900 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-200 transition-colors">
                        <SelectValue placeholder={t('devis.selectType')} />
                      </SelectTrigger>
                      <SelectContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 z-[9999] transition-colors">
                        {prixTypes.length === 0 ? (
                          <div className="px-4 py-2 text-slate-400">{t('devis.loading')}</div>
                        ) : (
                          prixTypes.map(type => (
                            <SelectItem key={type.id} value={type.id} className="text-slate-200 hover:bg-slate-800">
                              {type.label}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    {formData.type_beton && (
                      <>
                        <p className="text-xs text-slate-400 mt-1">
                          {prixTypes.find(t => t.label === formData.type_beton)?.description}
                        </p>
                        <div className="mt-2 p-2 bg-blue-900/20 border border-blue-700 rounded">
                          <p className="text-xs text-blue-300">
                            {t('devis.priceInfoTiers')}
                          </p>
                        </div>
                      </>
                    )}
                  </div>

                  <div>
                    <Label className="text-slate-700 dark:text-slate-300 transition-colors">{t('devis.surface')}</Label>
                    <Input
                      id="devis-surface"
                      type="number"
                      name="surface"
                      value={formData.surface}
                      onChange={handleChange}
                      required
                      min="1"
                      step="0.01"
                      className="bg-slate-50 dark:bg-slate-900 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-200 transition-colors"
                      placeholder={t('devis.surfacePlaceholder')}
                    />
                  </div>

                  <div>
                    <Label className="text-slate-700 dark:text-slate-300 transition-colors">
                      {t('devis.thickness')}
                      <span className="text-xs text-slate-400 ml-2">{t('devis.thicknessStandard')}</span>
                    </Label>
                    <Input
                      id="devis-epaisseur"
                      type="number"
                      name="epaisseur_cm"
                      value={formData.epaisseur_cm}
                      onChange={handleChange}
                      required
                      min="8"
                      max="30"
                      step="1"
                      className="bg-slate-50 dark:bg-slate-900 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-200 transition-colors"
                      placeholder={t('devis.thicknessPlaceholder')}
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      {t('devis.thicknessInfo')}
                    </p>
                  </div>

                  <div>
                    <Label className="text-slate-700 dark:text-slate-300 transition-colors">{t('devis.pricePerSqm')}</Label>
                    <Input
                      id="devis-prix-m2"
                      type="number"
                      name="prix_m2"
                      value={formData.prix_m2}
                      onChange={handleChange}
                      required
                      min="1"
                      step="0.01"
                      className="bg-slate-50 dark:bg-slate-900 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-200 transition-colors"
                      placeholder={t('devis.priceAutoCalculated')}
                    />
                    {formData.surface && formData.prix_m2 && (
                      <p className="text-xs text-green-400 mt-1">
                        {t('devis.priceFor')} {formData.surface}m² → {formData.prix_m2}€/m² 
                        {parseFloat(formData.surface) <= 40 && ` ${t('devis.priceTier0')}`}
                        {parseFloat(formData.surface) >= 41 && parseFloat(formData.surface) <= 70 && ` ${t('devis.priceTier1')}`}
                        {parseFloat(formData.surface) >= 71 && parseFloat(formData.surface) <= 150 && ` ${t('devis.priceTier2')}`}
                        {parseFloat(formData.surface) >= 151 && ` ${t('devis.priceTier3')}`}
                      </p>
                    )}
                  </div>

                  {/* Terasament Section */}
                  <div className="border-t border-slate-300 dark:border-slate-700 pt-4 mt-4 transition-colors">
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          id="besoin_terasament"
                          name="besoin_terasament"
                          checked={formData.besoin_terasament}
                          onChange={handleChange}
                          className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-slate-400 dark:border-slate-600 rounded cursor-pointer transition-colors"
                        />
                        <label htmlFor="besoin_terasament" className="text-slate-900 dark:text-slate-300 cursor-pointer flex-1 transition-colors">
                          <div className="flex items-center gap-2">
                            <span className="text-blue-600 dark:text-blue-400 transition-colors">🏗️</span>
                            <span className="font-semibold">{t('devis.groundPreparation')}</span>
                          </div>
                          <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 transition-colors">
                            {t('devis.groundPreparationCost')}
                          </p>
                        </label>
                      </div>
                      
                      {formData.besoin_terasament && (
                        <div className="ml-7 bg-slate-100 dark:bg-slate-800/50 p-4 rounded-lg border border-slate-300 dark:border-slate-700 transition-colors">
                          <Label className="text-slate-900 dark:text-slate-300 transition-colors">
                            {t('devis.groundSurface')}
                            <span className="text-xs text-slate-600 dark:text-slate-400 ml-2 transition-colors">{t('devis.groundSurfaceInfo')}</span>
                          </Label>
                          <Input
                            id="devis-surface-terasament"
                            type="number"
                            name="surface_terasament"
                            value={formData.surface_terasament}
                            onChange={handleChange}
                            required={formData.besoin_terasament}
                            min="1"
                            max={formData.surface || 999999}
                            step="0.01"
                            className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-200 transition-colors mt-2"
                            placeholder={t('devis.groundSurfacePlaceholder')}
                          />
                          <p className="text-xs text-slate-600 dark:text-slate-400 mt-2 transition-colors" dangerouslySetInnerHTML={{ __html: t('devis.groundSurfaceExample') }}>
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Pump Section */}
                  <div className="border-t border-slate-300 dark:border-slate-700 pt-4 mt-4 transition-colors">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="besoin_pompe"
                        name="besoin_pompe"
                        checked={formData.besoin_pompe}
                        onChange={handleChange}
                        className="mt-1 h-5 w-5 text-terracotta-600 focus:ring-terracotta-500 border-slate-400 dark:border-slate-600 rounded cursor-pointer transition-colors"
                      />
                      <label htmlFor="besoin_pompe" className="text-slate-900 dark:text-slate-300 cursor-pointer transition-colors">
                        <div className="flex items-center gap-2">
                          <TruckIcon className="w-4 h-4 text-terracotta-600 dark:text-terracotta-400 transition-colors" />
                          <span className="font-semibold">{t('devis.pumpRequired')}</span>
                        </div>
                        <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 transition-colors">
                          {t('devis.pumpCost')}
                        </p>
                      </label>
                    </div>

                    {formData.besoin_pompe && (
                      <div className="mt-3 ml-7">
                        <Label className="text-slate-700 dark:text-slate-300 transition-colors">{t('devis.volumeToPump')}</Label>
                        <Input
                          id="devis-volume-beton"
                          type="number"
                          name="volume_beton"
                          value={formData.volume_beton}
                          onChange={handleChange}
                          required={formData.besoin_pompe}
                          min="0.01"
                          max="32"
                          step="0.01"
                          className="bg-slate-50 dark:bg-slate-900 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-200 transition-colors"
                          placeholder={t('devis.volumeAutoCalculated')}
                        />
                        <div className="mt-2 p-2 bg-blue-100 dark:bg-blue-900/20 border border-blue-400 dark:border-blue-700 rounded transition-colors">
                          <p className="text-xs text-blue-800 dark:text-blue-300 transition-colors" dangerouslySetInnerHTML={{ __html: `${t('devis.volumeInfo')} (${formData.epaisseur_cm || 12}cm / 100) = ${formData.volume_beton} m³<br/><span class="text-blue-700 dark:text-blue-200 transition-colors">${t('devis.volumeModifiable')}</span>` }}>
                          </p>
                        </div>
                        {showWarning && (
                          <div className="flex items-start gap-2 mt-2 p-2 bg-amber-100 dark:bg-amber-900/20 border border-amber-400 dark:border-amber-700 rounded transition-colors">
                            <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-500 mt-0.5 flex-shrink-0 transition-colors" />
                            <p className="text-xs text-amber-800 dark:text-amber-300 transition-colors">
                              {t('devis.volumeWarning')}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <Button
                    type="button"
                    onClick={calculatePrice}
                    className="w-full bg-orange-600 hover:bg-orange-700 dark:bg-terracotta-600 dark:hover:bg-terracotta-700 text-white font-semibold transition-colors"
                    disabled={!formData.surface || !formData.prix_m2 || (formData.besoin_pompe && !formData.volume_beton)}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    {t('devis.calculate')}
                  </Button>
                </CardContent>
              </Card>

              {/* Right Column - Contact Info & Results */}
              <div className="space-y-6">
                {/* Calculation Result */}
                {calculation && (
                  <Card className="bg-gradient-to-br from-terracotta-900/30 to-clay-900/30 border-terracotta-700">
                    <CardHeader>
                      <CardTitle className="text-slate-200">{t('devis.totalEstimate')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {calculation.volume_m3 && (
                        <div className="bg-slate-800/50 p-3 rounded-lg border border-terracotta-600/30">
                          <div className="flex justify-between items-center">
                            <span className="text-slate-200">{t('devis.volumeConcrete')}</span>
                            <span className="font-bold text-lg text-terracotta-400">{calculation.volume_m3} m³</span>
                          </div>
                          <div className="text-xs text-slate-400 mt-1">
                            ({formData.surface} m² × {calculation.epaisseur_cm}cm {t('devis.volumeDetail')})
                          </div>
                        </div>
                      )}
                      <div className="flex justify-between text-slate-300">
                        <span>{t('devis.concreteCost')}</span>
                        <span className="font-semibold text-slate-100">{calculation.cout_lucrare} €</span>
                      </div>
                      {formData.besoin_terasament && parseFloat(calculation.cout_terasament) > 0 && (
                        <>
                          <div className="flex justify-between text-slate-300">
                            <span>{t('devis.groundCost')}</span>
                            <span className="font-semibold text-slate-100">{calculation.cout_terasament} €</span>
                          </div>
                          <div className="text-xs text-slate-400 pl-2">
                            ({formData.surface_terasament || 0} m² {t('devis.groundCostDetail')})
                          </div>
                        </>
                      )}
                      {formData.besoin_pompe && parseFloat(calculation.cout_pompe) > 0 && (
                        <>
                          <div className="flex justify-between text-slate-300">
                            <span>{t('devis.pumpCost2')}</span>
                            <span className="font-semibold text-slate-100">{calculation.cout_pompe} €</span>
                          </div>
                          <div className="text-xs text-slate-400 pl-2">
                            (500€ + {formData.volume_beton} m³ {t('devis.pumpCostDetail')})
                          </div>
                        </>
                      )}
                      <div className="border-t border-slate-700 pt-3 flex justify-between text-xl">
                        <span className="font-bold text-slate-100">{t('devis.total')}</span>
                        <span className="font-bold text-green-400">{calculation.total_estime} €</span>
                      </div>
                      <p className="text-xs text-slate-400 text-center">
                        {t('devis.estimateNote')}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Contact form removed - data collected via modal before showing result */}
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <Card className="mt-8 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 transition-colors">
            <CardHeader>
              <CardTitle className="text-slate-900 dark:text-slate-200 transition-colors">{t('devis.faq')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-slate-200 mb-1 transition-colors">{t('devis.faqQ1')}</h3>
                <p className="text-sm text-slate-700 dark:text-slate-400 transition-colors">
                  {t('devis.faqA1')}
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-slate-200 mb-1 transition-colors">{t('devis.faqQ2')}</h3>
                <p className="text-sm text-slate-700 dark:text-slate-400 transition-colors">
                  {t('devis.faqA2')}
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-slate-200 mb-1 transition-colors">{t('devis.faqQ3')}</h3>
                <p className="text-sm text-slate-700 dark:text-slate-400 transition-colors" dangerouslySetInnerHTML={{ __html: t('devis.faqA3') }}></p>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-slate-200 mb-1 transition-colors">{t('devis.faqQ4')}</h3>
                <p className="text-sm text-slate-700 dark:text-slate-400 transition-colors">
                  {t('devis.faqA4')}
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-slate-200 mb-1 transition-colors">{t('devis.faqQ5')}</h3>
                <p className="text-sm text-slate-700 dark:text-slate-400 transition-colors">
                  {t('devis.faqA5')}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

        {/* Data Collection Modal */}
        {showDataModal && pendingCalculation && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <Card className="max-w-md w-full bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 shadow-2xl relative">
              {/* Close button (X) - top right corner */}
              <button
                onClick={handleModalClose}
                className="absolute top-4 right-4 p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
              
              <CardHeader>
                <CardTitle className="text-2xl text-slate-900 dark:text-white flex items-center gap-2 pr-8">
                  <Calculator className="w-6 h-6 text-[#C97249]" />
                  {t('devis.modalTitle')}
                </CardTitle>
                <CardDescription className="text-slate-600 dark:text-slate-300">
                  {t('devis.modalDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Email (Required) */}
                <div>
                  <Label htmlFor="modal-email" className="text-slate-900 dark:text-slate-200">
                    {t('devis.email')} *
                  </Label>
                  <Input
                    id="modal-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="votre.email@exemple.com"
                    className="mt-1"
                  />
                </div>

                {/* Prénom (Optional) */}
                <div>
                  <Label htmlFor="modal-prenom" className="text-slate-900 dark:text-slate-200">
                    {t('devis.firstName')}
                  </Label>
                  <Input
                    id="modal-prenom"
                    type="text"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleChange}
                    placeholder={t('devis.firstNamePlaceholder')}
                    className="mt-1"
                  />
                </div>

                {/* Ville (Required) */}
                <div>
                  <Label htmlFor="modal-ville" className="text-slate-900 dark:text-slate-200">
                    {t('devis.city')} *
                  </Label>
                  <VilleAutocomplete
                    id="modal-ville"
                    name="ville"
                    value={formData.ville}
                    onChange={handleChange}
                    required
                    placeholder="Orange, Avignon, Carpentras..."
                    className="mt-1"
                  />
                </div>

                {/* Téléphone (Optional) */}
                <div>
                  <Label htmlFor="modal-telephone" className="text-slate-900 dark:text-slate-200">
                    {t('devis.phone')}
                  </Label>
                  <Input
                    id="modal-telephone"
                    type="tel"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleChange}
                    placeholder="06 XX XX XX XX"
                    className="mt-1"
                  />
                </div>

                {/* GDPR Consent */}
                <div className="flex items-start gap-3 p-4 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
                  <input
                    type="checkbox"
                    id="modal-gdpr"
                    name="gdpr_consent"
                    checked={formData.gdpr_consent}
                    onChange={handleChange}
                    required
                    className="mt-1 h-4 w-4 text-[#C97249] focus:ring-[#C97249] border-slate-300 dark:border-slate-500 rounded cursor-pointer"
                  />
                  <label htmlFor="modal-gdpr" className="text-sm text-slate-700 dark:text-slate-200 cursor-pointer">
                    {t('devis.gdprConsent')}{' '}
                    <Link to="/confidentialite" target="_blank" className="text-[#C97249] font-semibold underline hover:text-[#B8624A]">
                      {t('devis.privacyPolicy')}
                    </Link>
                    . *
                  </label>
                </div>

                {/* Single Primary CTA */}
                <div className="pt-2">
                  <AntiSpamFields ref={antiSpamRef} onTurnstileVerify={setTurnstileToken} />
                  <Button
                    type="button"
                    onClick={handleRevealCalculation}
                    className="w-full bg-[#C97249] hover:bg-[#B8624A] text-white font-semibold py-3 text-lg mt-3"
                    disabled={isSubmitting || !formData.email || !formData.gdpr_consent}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        {t('devis.sending')}
                      </span>
                    ) : (
                      t('devis.submitAndReveal')
                    )}
                  </Button>
                </div>

                <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
                  {t('devis.modalNote')}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

    </>
  );
}
